import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalBody, Button, FormGroup, Label, Input, Row, Col, FormFeedback } from 'reactstrap';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { MediaZone } from './MediaZone';
import { MediaGallery } from './MediaGallery';
import { StatusPill } from './StatusPill';
import {
  getPreMoveoutUnits,
  createPreMoveoutInspection,
  updatePreMoveoutInspection,
  submitPreMoveoutInspection,
  uploadPreMoveoutFile,
  getPreMoveoutSignedUrls,
  deletePreMoveoutAttachment,
} from '../Helpers/Api';
import {
  REPAIR_CATEGORIES,
  CLEANLINESS_RATINGS,
  fmtDate,
  fmtTime,
  toDateInput,
  todayInput,
  escapeHtml,
} from '../Helpers/format';

const OTHER_UNIT = '__other__';

const STEPS = [
  { n: 1, label: 'Unit & dates' },
  { n: 2, label: 'Cleanliness' },
  { n: 3, label: 'Repairs' },
  { n: 4, label: 'Review' },
];

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    minHeight: 38,
    borderColor: isFocused ? '#0e578e' : '#e9e9ef',
    boxShadow: isFocused ? '0 0 0 0.15rem rgba(14, 87, 142, 0.25)' : 'none',
    backgroundColor: isDisabled ? '#f8f9fa' : base.backgroundColor,
    '&:hover': { borderColor: isFocused ? '#0e578e' : '#ced4da' },
  }),
};

let repairSeq = 0;
const newRepairRow = (partial = {}) => ({
  localId: `r${Date.now()}_${repairSeq++}`,
  _id: partial._id || null,
  category: partial.category || '',
  description: partial.description || '',
  attachments: Array.isArray(partial.attachments) ? [...partial.attachments] : [],
  pending: [],
});

const emptyForm = () => ({
  unit_number: '',
  pre_inspection_date: todayInput(),
  moveout_date: '',
  moveout_time: '',
  cleanliness: { rating: '', notes: '', attachments: [] },
  repairs: [],
  damages_notes: '',
  comments: '',
});

const formFromDoc = (doc) => ({
  unit_number: doc.unit_number || '',
  pre_inspection_date: toDateInput(doc.pre_inspection_date) || todayInput(),
  moveout_date: toDateInput(doc.moveout_date),
  moveout_time: doc.moveout_time || '',
  cleanliness: {
    rating: doc.cleanliness?.rating || '',
    notes: doc.cleanliness?.notes || '',
    attachments: [...(doc.cleanliness?.attachments || [])],
  },
  repairs: (doc.repairs || []).map((r) =>
    newRepairRow({ _id: r._id, category: r.category, description: r.description, attachments: r.attachments })
  ),
  damages_notes: doc.damages_notes || '',
  comments: doc.comments || '',
});

/* SweetAlert progress helper: one loading popup whose title/text we update in place. */
const progress = {
  show(title, text) {
    if (Swal.isVisible() && Swal.isLoading()) {
      const t = Swal.getTitle();
      if (t) t.textContent = title;
      const c = Swal.getHtmlContainer();
      if (c) c.textContent = text || ' ';
      return;
    }
    Swal.fire({
      title,
      text: text || ' ',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
  },
};

const problemsHtml = (problems) =>
  `<ul class="text-start mb-0 ps-3">${problems.map((p) => `<li>${escapeHtml(p)}</li>`).join('')}</ul>`;

/**
 * Full-screen 4-step create/edit form.
 *
 * Props
 * - isOpen, onClose()
 * - mode: 'create' | 'edit'; doc (edit mode)
 * - propertyId, buildingName, propertyTitle (create mode context; edit mode reads them from the doc)
 * - categories: [string] (falls back to the fixed list)
 * - onSaved(doc, { submitted }) — called after every successful persist (also when a submit failed
 *   validation but the draft was stored) so the parent can refresh list/detail.
 * - zIndex
 */
const PreMoveoutFormModal = ({
  isOpen,
  onClose,
  mode = 'create',
  doc = null,
  propertyId,
  buildingName,
  propertyTitle,
  categories,
  onSaved,
  zIndex = 1050,
}) => {
  const isEdit = mode === 'edit' && !!doc;

  const ctx = useMemo(() => {
    if (isEdit) {
      const p = doc.property_details?.property_id;
      return {
        propertyId: (p && typeof p === 'object' ? p._id : p) || propertyId || '',
        propertyTitle: (p && typeof p === 'object' ? p.title : '') || propertyTitle || '',
        buildingName: doc.property_details?.building_name || buildingName || '',
      };
    }
    return { propertyId: propertyId || '', propertyTitle: propertyTitle || '', buildingName: buildingName || '' };
  }, [isEdit, doc, propertyId, propertyTitle, buildingName]);

  const categoryOptions = useMemo(
    () => (categories && categories.length ? categories : REPAIR_CATEGORIES).map((c) => ({ value: c, label: c })),
    [categories]
  );

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [cleanPending, setCleanPending] = useState([]);
  const [unitMode, setUnitMode] = useState('list');
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [signedUrls, setSignedUrls] = useState({});
  const [docId, setDocId] = useState(null);
  const [docStatus, setDocStatus] = useState('draft');
  const [dirty, setDirty] = useState(false);
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);

  const isDraft = !docStatus || docStatus === 'draft';

  /* ------------------------------------------------ init on open */
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setTouched({});
    setDirty(false);
    setBusy(false);
    setCleanPending([]);
    setUnitMode('list');
    if (isEdit) {
      setForm(formFromDoc(doc));
      setDocId(doc._id);
      setDocStatus(doc.status || 'draft');
    } else {
      setForm(emptyForm());
      setDocId(null);
      setDocStatus('draft');
    }
  }, [isOpen, isEdit, doc]);

  /* ------------------------------------------------ units */
  useEffect(() => {
    if (!isOpen || !ctx.propertyId) return;
    let alive = true;
    setUnitsLoading(true);
    getPreMoveoutUnits(ctx.propertyId, ctx.buildingName)
      .then((rows) => alive && setUnits(Array.isArray(rows) ? rows : []))
      .catch(() => alive && setUnits([]))
      .finally(() => alive && setUnitsLoading(false));
    return () => {
      alive = false;
    };
  }, [isOpen, ctx.propertyId, ctx.buildingName]);

  // Once units are loaded, a preset unit (edit mode) that is not on the mastersheet list switches
  // the picker to free text; one that is on the list uses the select.
  useEffect(() => {
    if (!isOpen || unitsLoading || !form.unit_number) return;
    const inList = units.some((u) => String(u.unitNo) === String(form.unit_number));
    setUnitMode(inList ? 'list' : 'other');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitsLoading, units, isOpen]);

  const unitOptions = useMemo(
    () => [
      ...units.map((u) => ({
        value: String(u.unitNo),
        label: u.tenant ? `${u.unitNo} — ${u.tenant}` : String(u.unitNo),
      })),
      { value: OTHER_UNIT, label: 'Other / type a unit' },
    ],
    [units]
  );

  /* ------------------------------------------------ signed urls for existing keys */
  const fetchedKeysRef = useRef(new Set());
  useEffect(() => {
    if (!isOpen) {
      fetchedKeysRef.current = new Set();
      setSignedUrls({});
      return;
    }
    const keys = [
      ...form.cleanliness.attachments,
      ...form.repairs.flatMap((r) => r.attachments),
    ].filter((k) => k && !fetchedKeysRef.current.has(k));
    if (!keys.length) return;
    keys.forEach((k) => fetchedKeysRef.current.add(k));
    getPreMoveoutSignedUrls(keys)
      .then((urls) => setSignedUrls((prev) => ({ ...prev, ...(urls || {}) })))
      .catch(() => toast.error('Some previews failed to load'));
  }, [isOpen, form.cleanliness.attachments, form.repairs]);

  /* ------------------------------------------------ field helpers */
  const update = useCallback((patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  }, []);
  const updateClean = useCallback((patch) => {
    setForm((f) => ({ ...f, cleanliness: { ...f.cleanliness, ...patch } }));
    setDirty(true);
  }, []);
  const updateRepair = useCallback((localId, patch) => {
    setForm((f) => ({
      ...f,
      repairs: f.repairs.map((r) => (r.localId === localId ? { ...r, ...patch } : r)),
    }));
    setDirty(true);
  }, []);
  const addRepair = () => {
    setForm((f) => ({ ...f, repairs: [...f.repairs, newRepairRow()] }));
    setDirty(true);
  };
  const removeRepair = async (row) => {
    const hasContent = row.category || row.description || row.attachments.length || row.pending.length;
    if (hasContent) {
      const r = await Swal.fire({
        title: 'Remove this repair?',
        text: row.attachments.length
          ? 'Its uploaded files will be removed when you save.'
          : 'This row and its files will be discarded.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#fd625e',
        confirmButtonText: 'Remove',
      });
      if (!r.isConfirmed) return;
    }
    row.pending.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
    setForm((f) => ({ ...f, repairs: f.repairs.filter((x) => x.localId !== row.localId) }));
    setDirty(true);
  };

  const setCleanPendingDirty = (next) => {
    setCleanPending(next);
    setDirty(true);
  };

  /* ------------------------------------------------ validation */
  const totalPendingFiles = cleanPending.length + form.repairs.reduce((n, r) => n + r.pending.length, 0);

  const unitMissing = !String(form.unit_number || '').trim();
  const moveoutMissing = !form.moveout_date;
  const step1Ok = !unitMissing && !moveoutMissing;

  const cleanFiles = form.cleanliness.attachments.length + cleanPending.length;
  const ratingMissing = !form.cleanliness.rating;
  const step2Ok = !ratingMissing && cleanFiles > 0;

  const repairIssues = form.repairs.map((r) => ({
    localId: r.localId,
    category: !r.category,
    description: r.category === 'Other' && !r.description.trim(),
    files: r.attachments.length + r.pending.length === 0,
  }));
  const step3Ok = repairIssues.every((i) => !i.category && !i.description && !i.files);

  const stepOk = { 1: step1Ok, 2: step2Ok, 3: step3Ok, 4: true };

  const goNext = () => {
    if (!stepOk[step]) {
      setTouched((t) => ({ ...t, [`step${step}`]: true }));
      return;
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));
  const jumpTo = (n) => {
    if (n < step) setStep(n);
  };

  /* ------------------------------------------------ closing */
  const requestClose = useCallback(async () => {
    if (busy) return;
    if (dirty || totalPendingFiles > 0) {
      const r = await Swal.fire({
        title: 'Discard changes?',
        text: 'Unsaved edits and files that were not uploaded will be lost.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#fd625e',
        confirmButtonText: 'Discard',
        cancelButtonText: 'Keep editing',
      });
      if (!r.isConfirmed) return;
    }
    onClose();
  }, [busy, dirty, totalPendingFiles, onClose]);

  /* ------------------------------------------------ payload */
  const buildPayload = () => ({
    property_details: { property_id: ctx.propertyId, building_name: ctx.buildingName },
    unit_number: String(form.unit_number || '').trim(),
    pre_inspection_date: form.pre_inspection_date || null,
    moveout_date: form.moveout_date || null,
    moveout_time: form.moveout_time || '',
    cleanliness: {
      rating: form.cleanliness.rating || '',
      notes: form.cleanliness.notes || '',
      attachments: form.cleanliness.attachments,
    },
    repairs: form.repairs.map((r) => ({
      ...(r._id ? { _id: r._id } : {}),
      category: r.category,
      description: r.description || '',
      attachments: r.attachments,
    })),
    damages_notes: form.damages_notes || '',
    comments: form.comments || '',
  });

  /**
   * Reconcile local state with a saved doc: adopt repair _ids and attachment keys,
   * drop the pending files that were uploaded. Text fields are left untouched.
   */
  const applySavedDoc = useCallback((saved, uploadedIds) => {
    if (!saved) return;
    setDocId(saved._id);
    setDocStatus(saved.status || 'draft');
    setCleanPending((prev) => prev.filter((p) => !uploadedIds.has(p.id)));
    setForm((f) => {
      const savedRepairs = saved.repairs || [];
      return {
        ...f,
        cleanliness: { ...f.cleanliness, attachments: [...(saved.cleanliness?.attachments || [])] },
        repairs: f.repairs.map((r, i) => {
          const match = (r._id && savedRepairs.find((s) => s._id === r._id)) || savedRepairs[i] || null;
          return {
            ...r,
            _id: match?._id || r._id,
            attachments: match ? [...(match.attachments || [])] : r.attachments,
            pending: r.pending.filter((p) => !uploadedIds.has(p.id)),
          };
        }),
      };
    });
  }, []);

  /* ------------------------------------------------ persist (save draft / submit) */
  const persist = async ({ submit }) => {
    if (busy) return;
    if (unitMissing) {
      setStep(1);
      setTouched((t) => ({ ...t, step1: true }));
      toast.warn('Enter the unit number before saving');
      return;
    }
    if (submit && !(step1Ok && step2Ok && step3Ok)) {
      const firstBad = [1, 2, 3].find((n) => !stepOk[n]);
      setStep(firstBad);
      setTouched((t) => ({ ...t, [`step${firstBad}`]: true }));
      toast.warn('Complete the highlighted step before submitting');
      return;
    }

    const uploadedIds = new Set();
    let saved = null;
    let id = docId;
    setBusy(true);
    try {
      progress.show(isEdit || docId ? 'Saving changes…' : 'Saving…');
      const payload = buildPayload();
      saved = id ? await updatePreMoveoutInspection(id, payload) : await createPreMoveoutInspection(payload);
      if (!saved || !saved._id) throw new Error('The server did not return the saved inspection');
      id = saved._id;
      setDocId(id);

      const total = totalPendingFiles;
      if (total > 0) {
        let done = 0;
        const label = `Uploading ${total} file${total > 1 ? 's' : ''}…`;
        progress.show(label, `0 of ${total} done`);
        for (const p of cleanPending) {
          saved = await uploadPreMoveoutFile(id, { target: 'cleanliness', file: p.file });
          uploadedIds.add(p.id);
          done += 1;
          progress.show(label, `${done} of ${total} done`);
        }
        const savedRepairs = saved.repairs || [];
        for (let i = 0; i < form.repairs.length; i += 1) {
          const row = form.repairs[i];
          if (!row.pending.length) continue;
          const target = (row._id && savedRepairs.find((s) => s._id === row._id)) || savedRepairs[i];
          if (!target || !target._id) {
            throw new Error(`Could not match repair #${i + 1} to the saved record. Reopen the inspection and try again.`);
          }
          for (const p of row.pending) {
            saved = await uploadPreMoveoutFile(id, { target: 'repair', repairId: target._id, file: p.file });
            uploadedIds.add(p.id);
            done += 1;
            progress.show(label, `${done} of ${total} done`);
          }
        }
      }

      if (submit) {
        progress.show('Submitting…');
        saved = await submitPreMoveoutInspection(id);
      }

      applySavedDoc(saved, uploadedIds);
      setDirty(false);
      if (onSaved) onSaved(saved, { submitted: !!submit });
      await Swal.fire({
        icon: 'success',
        title: submit ? 'Inspection submitted' : isDraft ? 'Draft saved' : 'Changes saved',
        text: submit
          ? `Unit ${saved.unit_number} is ready for vendor assignment.`
          : `Unit ${saved.unit_number} was saved.`,
        timer: 1600,
        showConfirmButton: false,
      });
      onClose();
    } catch (err) {
      // Whatever was persisted before the failure stays persisted; reflect it locally so a retry
      // does not create a duplicate or re-upload files.
      if (saved) {
        applySavedDoc(saved, uploadedIds);
        if (onSaved) onSaved(saved, { submitted: false });
      }
      if (err && Array.isArray(err.problems) && err.problems.length) {
        Swal.fire({
          icon: 'warning',
          title: 'Not ready to submit',
          html: `<p class="mb-2">The draft was saved. Fix the following and submit again:</p>${problemsHtml(err.problems)}`,
          confirmButtonColor: '#0e578e',
        });
      } else {
        Swal.fire({ icon: 'error', title: 'Could not save', text: err?.message || 'Please try again.' });
      }
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------ remove an uploaded file (edit mode) */
  const removeExisting = async ({ target, key, repair }) => {
    if (!docId) return;
    const r = await Swal.fire({
      title: 'Remove this file?',
      text: 'It is deleted from the inspection immediately.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#fd625e',
      confirmButtonText: 'Remove',
    });
    if (!r.isConfirmed) return;
    try {
      const updated = await deletePreMoveoutAttachment(docId, { target, repairId: repair?._id, key });
      applySavedDoc(updated, new Set());
      if (onSaved) onSaved(updated, { submitted: false });
      toast.success('File removed');
    } catch (err) {
      toast.error(err.message || 'Failed to remove the file');
    }
  };

  /* ------------------------------------------------ render helpers */
  const selectedUnitOption =
    unitMode === 'other'
      ? unitOptions[unitOptions.length - 1]
      : unitOptions.find((o) => o.value === String(form.unit_number)) || null;

  const handleUnitSelect = (opt) => {
    if (!opt) {
      setUnitMode('list');
      update({ unit_number: '' });
      return;
    }
    if (opt.value === OTHER_UNIT) {
      setUnitMode('other');
      update({ unit_number: '' });
      return;
    }
    setUnitMode('list');
    update({ unit_number: opt.value });
  };

  const showStep1Errors = !!touched.step1;
  const showStep2Errors = !!touched.step2;
  const showStep3Errors = !!touched.step3;

  const ratingLabel = (v) => CLEANLINESS_RATINGS.find((r) => r.value === v)?.label || '—';

  const renderStepIndicator = () => (
    <ol className="pm-steps" aria-label="Form steps">
      {STEPS.map((s) => {
        const state = s.n === step ? 'is-active' : s.n < step ? 'is-done' : '';
        return (
          <li key={s.n}>
            <button
              type="button"
              className={`pm-step ${state}`.trim()}
              onClick={() => jumpTo(s.n)}
              disabled={s.n >= step}
              aria-current={s.n === step ? 'step' : undefined}
              aria-label={`Step ${s.n}: ${s.label}${s.n < step ? ' (completed)' : ''}`}
            >
              <span className="pm-step__num" aria-hidden="true">
                {s.n < step ? <FeatherIcon icon="check" size={13} /> : s.n}
              </span>
              <span className="pm-step__label">{s.label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );

  const renderStep1 = () => (
    <>
      <h6 className="pm-section-title">Unit &amp; dates</h6>
      <p className="pm-section-sub">
        {ctx.propertyTitle || 'Property'}
        {ctx.buildingName ? ` · ${ctx.buildingName}` : ''}
      </p>
      <Row className="g-3">
        <Col md={6}>
          <FormGroup>
            <Label for="pm-unit" className="form-label">
              Unit<span className="pm-req">*</span>
            </Label>
            <Select
              inputId="pm-unit"
              value={selectedUnitOption}
              onChange={handleUnitSelect}
              options={unitOptions}
              isLoading={unitsLoading}
              isClearable
              isDisabled={busy}
              placeholder={unitsLoading ? 'Loading units…' : 'Select a unit'}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              className="react-select-container"
              classNamePrefix="react-select"
              noOptionsMessage={() => 'No units found'}
            />
            {unitMode === 'other' && (
              <div className="mt-2">
                <Label for="pm-unit-text" className="form-label">
                  Unit number
                </Label>
                <Input
                  id="pm-unit-text"
                  type="text"
                  value={form.unit_number}
                  onChange={(e) => update({ unit_number: e.target.value })}
                  placeholder="e.g. 204B"
                  invalid={showStep1Errors && unitMissing}
                  disabled={busy}
                  autoFocus
                />
                <FormFeedback>Unit number is required</FormFeedback>
              </div>
            )}
            {unitMode !== 'other' && showStep1Errors && unitMissing && (
              <div className="text-danger small mt-1">Unit is required</div>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="pm-preinspection" className="form-label">
              Pre-inspection date
            </Label>
            <Input
              id="pm-preinspection"
              type="date"
              value={form.pre_inspection_date}
              onChange={(e) => update({ pre_inspection_date: e.target.value })}
              disabled={busy}
            />
            <div className="pm-hint">Defaults to today.</div>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="pm-moveout-date" className="form-label">
              Move-out date<span className="pm-req">*</span>
            </Label>
            <Input
              id="pm-moveout-date"
              type="date"
              value={form.moveout_date}
              onChange={(e) => update({ moveout_date: e.target.value })}
              invalid={showStep1Errors && moveoutMissing}
              disabled={busy}
              required
            />
            <FormFeedback>Move-out date is required</FormFeedback>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="pm-moveout-time" className="form-label">
              Move-out time
            </Label>
            <Input
              id="pm-moveout-time"
              type="time"
              value={form.moveout_time}
              onChange={(e) => update({ moveout_time: e.target.value })}
              disabled={busy}
            />
            <div className="pm-hint">Optional.</div>
          </FormGroup>
        </Col>
      </Row>
    </>
  );

  const renderStep2 = () => (
    <>
      <h6 className="pm-section-title">Cleanliness</h6>
      <p className="pm-section-sub">Rate the overall condition and attach photos or video as evidence.</p>

      <div className="pm-rating-group" role="group" aria-label="Cleanliness rating">
        {CLEANLINESS_RATINGS.map((r) => (
          <button
            key={r.value}
            type="button"
            className="pm-rating-btn"
            aria-pressed={form.cleanliness.rating === r.value}
            onClick={() => updateClean({ rating: r.value })}
            disabled={busy}
          >
            <FeatherIcon icon={r.icon} size={22} />
            {r.label}
          </button>
        ))}
      </div>
      {showStep2Errors && ratingMissing && <div className="text-danger small mb-3">Choose a rating</div>}

      <FormGroup>
        <Label for="pm-clean-notes" className="form-label">
          Notes
        </Label>
        <Input
          id="pm-clean-notes"
          type="textarea"
          rows={3}
          value={form.cleanliness.notes}
          onChange={(e) => updateClean({ notes: e.target.value })}
          placeholder="Anything the cleaner should know"
          disabled={busy}
        />
      </FormGroup>

      <FormGroup>
        <div className="form-label" id="pm-clean-media-label">
          Photos / video<span className="pm-req">*</span>
        </div>
        <MediaZone
          id="pm-clean-media"
          existingKeys={form.cleanliness.attachments}
          signedUrls={signedUrls}
          pendingFiles={cleanPending}
          onPendingChange={setCleanPendingDirty}
          onRemoveExisting={docId ? (key) => removeExisting({ target: 'cleanliness', key }) : undefined}
          required
          invalid={showStep2Errors && cleanFiles === 0}
          disabled={busy}
        />
        {cleanFiles === 0 && (
          <div className="pm-blocker" role="status">
            <FeatherIcon icon="alert-circle" size={16} />
            <span>Add at least one photo or video of the unit's condition to continue.</span>
          </div>
        )}
      </FormGroup>
    </>
  );

  const renderStep3 = () => (
    <>
      <h6 className="pm-section-title">Repairs</h6>
      <p className="pm-section-sub">
        One row per item that needs fixing. Each repair needs at least one photo or video. Leave empty if nothing needs
        repair.
      </p>

      {form.repairs.map((row, idx) => {
        const issue = repairIssues.find((i) => i.localId === row.localId) || {};
        return (
          <div className="pm-repair-card" key={row.localId}>
            <div className="pm-repair-card__head">
              <strong>Repair {idx + 1}</strong>
              <button
                type="button"
                className="pm-icon-btn pm-icon-btn--danger"
                onClick={() => removeRepair(row)}
                aria-label={`Remove repair ${idx + 1}`}
                title="Remove repair"
                disabled={busy}
              >
                <FeatherIcon icon="trash-2" size={16} />
              </button>
            </div>
            <Row className="g-3">
              <Col md={4}>
                <FormGroup>
                  <Label for={`pm-rep-cat-${row.localId}`} className="form-label">
                    Category<span className="pm-req">*</span>
                  </Label>
                  <Select
                    inputId={`pm-rep-cat-${row.localId}`}
                    value={categoryOptions.find((o) => o.value === row.category) || null}
                    onChange={(opt) => updateRepair(row.localId, { category: opt ? opt.value : '' })}
                    options={categoryOptions}
                    placeholder="Select category"
                    isDisabled={busy}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                  {showStep3Errors && issue.category && <div className="text-danger small mt-1">Category is required</div>}
                </FormGroup>
              </Col>
              <Col md={8}>
                <FormGroup>
                  <Label for={`pm-rep-desc-${row.localId}`} className="form-label">
                    Description{row.category === 'Other' && <span className="pm-req">*</span>}
                  </Label>
                  <Input
                    id={`pm-rep-desc-${row.localId}`}
                    type="text"
                    value={row.description}
                    onChange={(e) => updateRepair(row.localId, { description: e.target.value })}
                    placeholder={row.category === 'Other' ? 'Describe the repair' : 'Optional details, location, size'}
                    invalid={showStep3Errors && issue.description}
                    disabled={busy}
                  />
                  <FormFeedback>Describe the repair when the category is Other</FormFeedback>
                </FormGroup>
              </Col>
              <Col xs={12}>
                <div className="form-label">
                  Photos / video<span className="pm-req">*</span>
                </div>
                <MediaZone
                  id={`pm-rep-media-${row.localId}`}
                  existingKeys={row.attachments}
                  signedUrls={signedUrls}
                  pendingFiles={row.pending}
                  onPendingChange={(next) => updateRepair(row.localId, { pending: next })}
                  onRemoveExisting={
                    docId && row._id ? (key) => removeExisting({ target: 'repair', key, repair: row }) : undefined
                  }
                  required
                  invalid={showStep3Errors && issue.files}
                  disabled={busy}
                />
              </Col>
            </Row>
          </div>
        );
      })}

      <Button type="button" color="light" onClick={addRepair} disabled={busy} className="mb-3">
        <FeatherIcon icon="plus" size={15} className="me-1" />
        Add repair
      </Button>

      {!step3Ok && (
        <div className="pm-blocker" role="status">
          <FeatherIcon icon="alert-circle" size={16} />
          <span>Every repair needs a category, a description when the category is Other, and at least one file.</span>
        </div>
      )}

      <FormGroup className="mt-4">
        <Label for="pm-damages" className="form-label">
          Damages notes
        </Label>
        <Input
          id="pm-damages"
          type="textarea"
          rows={3}
          value={form.damages_notes}
          onChange={(e) => update({ damages_notes: e.target.value })}
          placeholder="Overall damage summary, tenant chargebacks, anything not covered above"
          disabled={busy}
        />
      </FormGroup>
    </>
  );

  const renderStep4 = () => (
    <>
      <h6 className="pm-section-title">Review</h6>
      <p className="pm-section-sub">Check the details below, add comments, then save or submit.</p>

      <div className="pm-review-grid pm-detail">
        <div className="pm-section">
          <div className="pm-section__head">
            <h6>Unit &amp; dates</h6>
            <Button color="link" size="sm" className="p-0" onClick={() => setStep(1)} disabled={busy}>
              Edit
            </Button>
          </div>
          <dl className="pm-kv">
            <dt>Property</dt>
            <dd>
              {ctx.propertyTitle || '—'}
              {ctx.buildingName ? ` · ${ctx.buildingName}` : ''}
            </dd>
            <dt>Unit</dt>
            <dd>{form.unit_number || '—'}</dd>
            <dt>Pre-inspection</dt>
            <dd>{fmtDate(form.pre_inspection_date)}</dd>
            <dt>Move-out</dt>
            <dd>
              {fmtDate(form.moveout_date)}
              {fmtTime(form.moveout_time) ? ` at ${fmtTime(form.moveout_time)}` : ''}
            </dd>
          </dl>
        </div>

        <div className="pm-section">
          <div className="pm-section__head">
            <h6>Cleanliness</h6>
            <Button color="link" size="sm" className="p-0" onClick={() => setStep(2)} disabled={busy}>
              Edit
            </Button>
          </div>
          <dl className="pm-kv">
            <dt>Rating</dt>
            <dd>{ratingLabel(form.cleanliness.rating)}</dd>
            <dt>Notes</dt>
            <dd>{form.cleanliness.notes || '—'}</dd>
            <dt>Media</dt>
            <dd>
              {cleanFiles} file{cleanFiles === 1 ? '' : 's'}
              {cleanPending.length ? ` (${cleanPending.length} to upload)` : ''}
            </dd>
          </dl>
          <MediaGallery keys={form.cleanliness.attachments} signedUrls={signedUrls} emptyText="" />
        </div>

        <div className="pm-section">
          <div className="pm-section__head">
            <h6>Repairs ({form.repairs.length})</h6>
            <Button color="link" size="sm" className="p-0" onClick={() => setStep(3)} disabled={busy}>
              Edit
            </Button>
          </div>
          {form.repairs.length === 0 ? (
            <p className="pm-note text-muted">No repairs recorded.</p>
          ) : (
            form.repairs.map((r, i) => (
              <div className="pm-repair-item" key={r.localId}>
                <div className="pm-repair-item__title">
                  {i + 1}. {r.category || 'Uncategorised'}
                </div>
                {r.description && <p className="pm-note">{r.description}</p>}
                <div className="text-muted small">
                  {r.attachments.length + r.pending.length} file
                  {r.attachments.length + r.pending.length === 1 ? '' : 's'}
                  {r.pending.length ? ` (${r.pending.length} to upload)` : ''}
                </div>
              </div>
            ))
          )}
          {form.damages_notes && (
            <>
              <hr className="my-2" />
              <div className="text-muted small fw-semibold">Damages notes</div>
              <p className="pm-note">{form.damages_notes}</p>
            </>
          )}
        </div>
      </div>

      <FormGroup className="mt-3">
        <Label for="pm-comments" className="form-label">
          Comments
        </Label>
        <Input
          id="pm-comments"
          type="textarea"
          rows={3}
          value={form.comments}
          onChange={(e) => update({ comments: e.target.value })}
          placeholder="Internal comments for the team"
          disabled={busy}
        />
      </FormGroup>
    </>
  );

  const title = isEdit ? `Edit pre-moveout inspection — Unit ${doc.unit_number || ''}` : 'New pre-moveout inspection';

  return (
    <Modal
      isOpen={isOpen}
      toggle={requestClose}
      className="modal-fullscreen"
      scrollable
      backdrop="static"
      keyboard={!busy}
      zIndex={zIndex}
      labelledBy="pm-form-title"
    >
      <div className="pm-modal-header">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div>
            <h5 id="pm-form-title">{title}</h5>
            <div className="pm-modal-sub">
              {ctx.propertyTitle || 'Property'}
              {ctx.buildingName ? ` · ${ctx.buildingName}` : ''}
            </div>
          </div>
          {(isEdit || docId) && <StatusPill status={docStatus} />}
        </div>
        <button type="button" className="pm-icon-btn" onClick={requestClose} aria-label="Close" disabled={busy}>
          <FeatherIcon icon="x" size={18} />
        </button>
      </div>

      <ModalBody>
        <div className="pm-form-body">
          {renderStepIndicator()}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>
      </ModalBody>

      <div className="pm-modal-footer">
        <div className="d-flex gap-2">
          <Button type="button" color="light" onClick={requestClose} disabled={busy}>
            Cancel
          </Button>
        </div>
        <div className="d-flex gap-2 flex-wrap justify-content-end">
          {step > 1 && (
            <Button type="button" color="light" onClick={goBack} disabled={busy}>
              <FeatherIcon icon="chevron-left" size={15} className="me-1" />
              Back
            </Button>
          )}
          <Button type="button" color="secondary" outline onClick={() => persist({ submit: false })} disabled={busy}>
            <FeatherIcon icon="save" size={15} className="me-1" />
            {isDraft ? 'Save as draft' : 'Save changes'}
          </Button>
          {step < 4 ? (
            <Button
              type="button"
              color="primary"
              onClick={goNext}
              disabled={busy || !stepOk[step]}
              aria-disabled={!stepOk[step]}
            >
              Next
              <FeatherIcon icon="chevron-right" size={15} className="ms-1" />
            </Button>
          ) : (
            isDraft && (
              <Button type="button" color="primary" onClick={() => persist({ submit: true })} disabled={busy}>
                <FeatherIcon icon="send" size={15} className="me-1" />
                Submit inspection
              </Button>
            )
          )}
        </div>
      </div>
    </Modal>
  );
};

export { PreMoveoutFormModal };
export default PreMoveoutFormModal;
